import React, { useMemo } from 'react';
import { Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { ReportRow, QueryResponse } from '../../../services/h312Api';

interface ResultTableProps {
  result: QueryResponse;
  groupBy: string;
}

const fmt = (v: number | null | undefined): string => {
  if (v == null || v === 0) return '';
  return v.toLocaleString('en-US', { maximumFractionDigits: 2 });
};

const columns: ColumnsType<ReportRow & { _key: string }> = [
  {
    title: '',
    dataIndex: 'fh110_mk',
    key: 'fh110_mk',
    width: 30,
    align: 'center',
    render: (v: string) => v === '*' ? <Tag color="red">*</Tag> : null,
  },
  {
    title: '請款單號',
    dataIndex: 'fh110_01',
    key: 'fh110_01',
    width: 80,
  },
  {
    title: '請款日期',
    dataIndex: 'fh110_11',
    key: 'fh110_11',
    width: 100,
  },
  {
    title: '廠商/員工',
    dataIndex: 'fh110_16',
    key: 'fh110_16',
    width: 120,
    ellipsis: true,
  },
  {
    title: '部門',
    dataIndex: 'fh110_37',
    key: 'fh110_37',
    width: 100,
    ellipsis: true,
  },
  {
    title: '類別',
    dataIndex: 'fh110_22',
    key: 'fh110_22',
    width: 80,
  },
  {
    title: '發票號碼',
    dataIndex: 'fh113_11',
    key: 'fh113_11',
    width: 100,
  },
  {
    title: '發票日期',
    dataIndex: 'fh113_12',
    key: 'fh113_12',
    width: 100,
  },
  {
    title: '單據號碼/費用名稱',
    dataIndex: 'fh111_11',
    key: 'fh111_11',
    width: 140,
    ellipsis: true,
  },
  {
    title: '採購員',
    dataIndex: 'fh111_111',
    key: 'fh111_111',
    width: 80,
  },
  {
    title: '未稅金額',
    dataIndex: 'fh111_15',
    key: 'fh111_15',
    width: 100,
    align: 'right',
    render: (v: number) => fmt(v),
  },
  {
    title: '稅額',
    dataIndex: 'fh111_22',
    key: 'fh111_22',
    width: 80,
    align: 'right',
    render: (v: number) => fmt(v),
  },
  {
    title: '請款人',
    dataIndex: 'fh110_91',
    key: 'fh110_91',
    width: 80,
  },
];

const ResultTable: React.FC<ResultTableProps> = ({ result, groupBy }) => {
  const tableData = useMemo(
    () =>
      (result.data || []).map((row, i) => ({
        ...row,
        _key: `${row.fh110_01}-${row.fh113_02}-${row.fh111_03}-${i}`,
      })),
    [result.data]
  );

  // Compute sort order based on groupBy
  const sortedColumns = useMemo(() => {
    const cols = [...columns];
    // Move the groupBy column to front for clarity
    if (groupBy === 'vendor') {
      // Already fine — vendor is early
    } else if (groupBy === 'dept') {
      // Move dept column earlier
      const deptIdx = cols.findIndex(c => c.key === 'fh110_37');
      if (deptIdx > 1) {
        const [deptCol] = cols.splice(deptIdx, 1);
        cols.splice(1, 0, deptCol);
      }
    } else if (groupBy === 'doc') {
      // Move invoice no to front
      const docIdx = cols.findIndex(c => c.key === 'fh110_01');
      if (docIdx > 1) {
        const [docCol] = cols.splice(docIdx, 1);
        cols.splice(1, 0, docCol);
      }
    }
    return cols;
  }, [groupBy]);

  // Compute totals
  const totalAmount = useMemo(() => {
    return (result.data || []).reduce((sum, row) => sum + (row.fh111_15 || 0), 0);
  }, [result.data]);

  const totalTax = useMemo(() => {
    return (result.data || []).reduce((sum, row) => sum + (row.fh111_22 || 0), 0);
  }, [result.data]);

  return (
    <div>
      <div className="mb-2 text-sm text-gray-600">
        共 {result.totalCount} 筆 | 未稅合計: {fmt(totalAmount)} | 稅額合計: {fmt(totalTax)}
        <span className="ml-4 text-red-500">* 表示未核准</span>
      </div>
      <Table
        columns={sortedColumns}
        dataSource={tableData}
        rowKey="_key"
        size="small"
        scroll={{ x: 1200, y: 500 }}
        pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'] }}
      />
    </div>
  );
};

export default ResultTable;
